/**
 * The session's order history (#1809, WS_DESIGN_1809 W7): the newest 2000 read once, then the userHistoricalOrders stream
 * (sessionRows.ts).
 *
 * Unlike fills there is no read by time, so what is missing costs the whole list again.
 *
 * The answer holds the newest rows, and mergeHistory keeps the newest HISTORY_MAX. So when more than that was missed the
 * older side falls away by itself, and no hole is ever shown.
 */
import { abortError } from '../hl/info';
import type { HistoricalOrder, UserHistoricalOrdersMsg } from '../hl/types';
import { FILL_BASE_WEIGHT, FILL_ROWS_PER_WEIGHT } from '../settlement/store';
import { historyKey, mergeHistory } from './orders/model';
import { SessionRows, type RowsStatus, type SessionRowsDeps } from './sessionRows';

export interface HistoryHeld extends RowsStatus {
  /** Newest status change first, one row per (oid, status). */
  history: HistoricalOrder[];
}

export class SessionHistory extends SessionRows<HistoryHeld, HistoricalOrder> {
  /** A read is owed: the download, or a gap. */
  private owed = true;

  constructor(deps: SessionRowsDeps) {
    super(deps, { history: [], loading: true, complete: false, error: null });
  }

  protected stream(): () => void {
    const { ws, user } = this.deps;
    return ws.subscribe({ type: 'userHistoricalOrders', user }, (msg) => this.onFrame(msg));
  }

  protected readOwed(): boolean {
    return this.owed;
  }

  protected keyOf(h: HistoricalOrder): string {
    return historyKey(h);
  }

  protected held(): readonly HistoricalOrder[] {
    return this.state.history;
  }

  protected merged(rows: HistoricalOrder[]): Partial<HistoryHeld> {
    return { history: mergeHistory(this.state.history, rows) };
  }

  private onFrame(msg: UserHistoricalOrdersMsg): void {
    if (Array.isArray(msg?.orderHistory)) this.onRows(msg.orderHistory, msg.isSnapshot === true);
  }

  protected read(): void {
    void this.fetch();
  }

  protected readMissing(): void {
    void this.fetch();
  }

  /** The first read queues on the pacer. A later one is a repeat refresh: it pays at once and may leave debt. */
  private async fetch(): Promise<void> {
    const { info, pacer, user } = this.deps;
    this.owed = true;
    const ac = this.beginRead();
    try {
      if (this.state.complete) pacer.charge(FILL_BASE_WEIGHT);
      else await pacer.take(FILL_BASE_WEIGHT, { signal: ac.signal });
      const got = await info.historicalOrders(user, { signal: ac.signal });
      if (ac.signal.aborted) throw abortError();
      const rows = Array.isArray(got) ? got : [];
      pacer.charge(Math.floor(rows.length / FILL_ROWS_PER_WEIGHT));
      this.owed = false;
      const missed = this.missedDuringRead(rows);
      this.publish({ ...this.merged(rows), loading: false, complete: true, error: null });
      if (missed) void this.fetch();
    } catch (e) {
      this.failed(ac, e);
    }
  }
}
